import React from "react";
import Select from "react-select";
import { Search, X } from "lucide-react";

import { Input } from "components/ui/input";

interface UserFilterProps {
  textValue: string;
  onClearSearch: () => void;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusFilterChange?: (selectedOption: any) => void;
  onVisibilityFilterChange?: (selectedOption: any) => void;
}

const calendarStatusOptions = [
  { value: "Hidden", label: "All Status" },
  { value: "Confirmed", label: "Confirmed Event" },
  { value: "Tentative", label: "Tentative Event" },
  { value: "Cancelled", label: "Cancelled Event" },
];

const calendarVisibilityOptions = [
  { value: "Hidden", label: "All Visibility" },
  { value: "Public", label: "Public Event" },
  { value: "Private", label: "Private Event" },
  { value: "Default", label: "Default Event" },
];

const reactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    borderColor: state.isFocused
      ? "hsl(var(--ring))"
      : "hsl(var(--border))",
    boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--ring) / 0.25)" : "none",
    backgroundColor: "hsl(var(--background))",
    fontSize: 14,
    "&:hover": { borderColor: "hsl(var(--border))" },
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid hsl(var(--border))",
    backgroundColor: "hsl(var(--popover))",
    overflow: "hidden",
    boxShadow: "0 10px 25px -5px rgb(0 0 0 / 0.08)",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 14,
    color: "hsl(var(--popover-foreground))",
    backgroundColor: state.isSelected
      ? "hsl(var(--primary) / 0.12)"
      : state.isFocused
        ? "hsl(var(--muted) / 0.6)"
        : "transparent",
    "&:active": { backgroundColor: "hsl(var(--primary) / 0.18)" },
  }),
  singleValue: (base: any) => ({
    ...base,
    color: "hsl(var(--foreground))",
  }),
};

const SearchFilter: React.FC<UserFilterProps> = ({
  onSearch,
  onClearSearch,
  onStatusFilterChange,
  onVisibilityFilterChange,
  textValue,
}) => {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_180px_180px_1fr]">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search by event title or guest email"
          autoComplete="off"
          onChange={onSearch}
          value={textValue}
          className="h-10 pl-8 pr-8"
        />
        {textValue && (
          <button
            type="button"
            onClick={onClearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <Select
        classNamePrefix="react-select"
        options={calendarStatusOptions}
        isSearchable={false}
        defaultValue={calendarStatusOptions[0]}
        onChange={onStatusFilterChange}
        menuPortalTarget={document.body}
        styles={{
          ...reactSelectStyles,
          menuPortal: (base: any) => ({ ...base, zIndex: 60 }),
        }}
      />

      <Select
        classNamePrefix="react-select"
        options={calendarVisibilityOptions}
        isSearchable={false}
        defaultValue={calendarVisibilityOptions[0]}
        onChange={onVisibilityFilterChange}
        menuPortalTarget={document.body}
        styles={{
          ...reactSelectStyles,
          menuPortal: (base: any) => ({ ...base, zIndex: 60 }),
        }}
      />
    </div>
  );
};

export default SearchFilter;
